import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import type { AnfrageStatus, AnfrageTyp, KonvertierungsTyp, SlaStatus } from '../lib/api-enums';
import { formatGermanDateTime } from '../lib/date-format';

interface Anfrage {
  id: string;
  eingangAm: string;
  quelle: string;
  externId: string | null;
  absenderEmail: string | null;
  absenderName: string | null;
  betreff: string | null;
  text: string | null;
  anfrageTyp: AnfrageTyp | null;
  kundeId: string | null;
  objektId: string | null;
  zugeordnetAnUserId: string | null;
  status: AnfrageStatus;
  slaReaktionBis: string | null;
  slaStatus: SlaStatus | null;
  konvertiertZuTyp: KonvertierungsTyp | null;
  konvertiertZuId: string | null;
  notizen: string | null;
  createdAt: string;
}

interface Kunde {
  id: string;
  kundennummer: string;
  firmenname: string;
}

interface Objekt {
  id: string;
  bezeichnung: string;
  kundeId: string;
}

interface User {
  id: string;
  email: string;
  displayName: string;
}

const TYP_OPTIONS: AnfrageTyp[] = [
  'reparatur',
  'wartung',
  'notdienst',
  'reklamation',
  'beratung',
  'aufmass',
  'sonstige',
];

const TYP_LABELS: Record<AnfrageTyp, string> = {
  reparatur: 'Reparatur',
  wartung: 'Wartung',
  notdienst: 'Notdienst',
  reklamation: 'Reklamation',
  beratung: 'Beratung',
  aufmass: 'Aufmaß',
  sonstige: 'Sonstige',
};

const STATUS_LABELS: Record<AnfrageStatus, string> = {
  neu: 'Neu',
  in_triage: 'In Triage',
  zugeordnet: 'Zugeordnet',
  konvertiert: 'Konvertiert',
  geschlossen: 'Geschlossen',
  dupliziert: 'Dupliziert',
};

export const Route = createFileRoute('/inbox/$id')({
  beforeLoad: () => requireAccess('/api/v1/inbox'),
  component: InboxDetailPage,
});

function InboxDetailPage() {
  const { id } = Route.useParams();
  const router = useRouter();

  const anfrageQ = useQuery({
    queryKey: ['inbox', id],
    queryFn: () => api<{ item: Anfrage }>(`/api/v1/inbox/${id}`),
  });

  const kundenQ = useQuery({
    queryKey: ['kunden', 'lookup'],
    queryFn: () => api<{ items: Kunde[] }>('/api/v1/kunden'),
  });
  const objekteQ = useQuery({
    queryKey: ['objekte', 'lookup'],
    queryFn: () => api<{ items: Objekt[] }>('/api/v1/objekte'),
  });
  const usersQ = useQuery({
    queryKey: ['users', 'lookup'],
    queryFn: () => api<{ items: User[] }>('/api/v1/users'),
  });

  const a = anfrageQ.data?.item;

  // Triage-Form-State (initial aus aktuellem Anfrage-Stand)
  const [anfrageTyp, setAnfrageTyp] = useState<AnfrageTyp | ''>('');
  const [kundeId, setKundeId] = useState('');
  const [objektId, setObjektId] = useState('');
  const [zugeordnetAnUserId, setZugeordnetAnUserId] = useState('');
  const [notizen, setNotizen] = useState('');
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (a) {
      setAnfrageTyp(a.anfrageTyp ?? '');
      setKundeId(a.kundeId ?? '');
      setObjektId(a.objektId ?? '');
      setZugeordnetAnUserId(a.zugeordnetAnUserId ?? '');
      setNotizen(a.notizen ?? '');
    }
  }, [a]);

  const objekteDesKunden = (objekteQ.data?.items ?? []).filter((o) => o.kundeId === kundeId);

  const triageMutation = useMutation({
    mutationFn: async () =>
      api<{ item: Anfrage }>(`/api/v1/inbox/${id}/triage`, {
        method: 'PATCH',
        body: {
          anfrageTyp: anfrageTyp || undefined,
          kundeId: kundeId || null,
          objektId: objektId || null,
          zugeordnetAnUserId: zugeordnetAnUserId || null,
          notizen: notizen || null,
        },
      }),
    onSuccess: () => anfrageQ.refetch(),
    onError: (err) => setError(err),
  });

  const convertMutation = useMutation({
    mutationFn: async (typ: KonvertierungsTyp) =>
      api<{ item: Anfrage }>(`/api/v1/inbox/${id}/convert`, {
        method: 'POST',
        body: { typ },
      }),
    onSuccess: () => anfrageQ.refetch(),
    onError: (err) => setError(err),
  });

  const closeMutation = useMutation({
    mutationFn: async (grund: string) =>
      api<{ item: Anfrage }>(`/api/v1/inbox/${id}/close`, {
        method: 'POST',
        body: { grund },
      }),
    onSuccess: async () => {
      await anfrageQ.refetch();
      await router.navigate({ to: '/inbox' });
    },
    onError: (err) => setError(err),
  });

  function handleTriage(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    triageMutation.mutate();
  }

  function handleClose() {
    const grund = window.prompt('Grund fuer Schliessung (z.B. Spam, irrelevant)?');
    if (grund && grund.trim()) closeMutation.mutate(grund.trim());
  }

  if (anfrageQ.isLoading) return <div className="text-sm text-slate-500">lädt …</div>;
  if (anfrageQ.isError || !a)
    return <ErrorBanner error={anfrageQ.error ?? 'Anfrage nicht gefunden'} />;

  const isLocked = a.status === 'konvertiert' || a.status === 'geschlossen';

  return (
    <div className="space-y-4">
      <div>
        <Link to="/inbox" className="text-sm text-brand-700 hover:underline">
          ← zurück zur Inbox
        </Link>
      </div>

      <PageHeader
        title={a.betreff ?? `Anfrage #${a.id}`}
        description={`${a.quelle} • Eingang ${formatGermanDateTime(a.eingangAm)} • Status: ${STATUS_LABELS[a.status]}`}
      />

      {error ? <ErrorBanner error={error} /> : null}

      {/* Header-Info */}
      <section className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 space-y-2 text-sm">
        <h2 className="text-sm font-semibold text-slate-900">Eingang</h2>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-y-1 gap-x-6">
          <DefRow label="Absender">
            {a.absenderName ?? '—'}{' '}
            {a.absenderEmail ? (
              <span className="text-slate-500">&lt;{a.absenderEmail}&gt;</span>
            ) : null}
          </DefRow>
          <DefRow label="Quelle">{a.quelle}</DefRow>
          {a.externId ? <DefRow label="Mail-ID">{a.externId}</DefRow> : null}
          <DefRow label="SLA-Frist">
            {a.slaReaktionBis ? formatGermanDateTime(a.slaReaktionBis) : '— offen bis Triage —'}
          </DefRow>
        </dl>
        {a.text ? (
          <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
            {a.text}
          </pre>
        ) : null}
      </section>

      {/* Konvertierungs-Info wenn schon konvertiert */}
      {a.status === 'konvertiert' ? (
        <section className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900">
          ✓ Konvertiert in {a.konvertiertZuTyp}
          {a.konvertiertZuId
            ? ` (#${a.konvertiertZuId})`
            : ' (Folgevorgang in Phase 5 noch nicht angelegt)'}
          .
        </section>
      ) : null}

      {/* Triage-Form (nur wenn nicht in Endzustand) */}
      {!isLocked ? (
        <form
          onSubmit={handleTriage}
          className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm space-y-3"
        >
          <h2 className="text-sm font-semibold text-slate-900">Triage</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <label className="text-xs font-medium text-slate-600">Anfrage-Typ</label>
              <Select
                value={anfrageTyp}
                onChange={(e) => setAnfrageTyp(e.target.value as AnfrageTyp | '')}
              >
                <option value="">— offen —</option>
                {TYP_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {TYP_LABELS[t]}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Kunde</label>
              <Select
                value={kundeId}
                onChange={(e) => {
                  setKundeId(e.target.value);
                  setObjektId('');
                }}
              >
                <option value="">— offen —</option>
                {(kundenQ.data?.items ?? []).map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.kundennummer} — {k.firmenname}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Objekt (optional)</label>
              <Select
                value={objektId}
                disabled={!kundeId}
                onChange={(e) => setObjektId(e.target.value)}
              >
                <option value="">— keines —</option>
                {objekteDesKunden.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.bezeichnung}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Bearbeiter</label>
              <Select
                value={zugeordnetAnUserId}
                onChange={(e) => setZugeordnetAnUserId(e.target.value)}
              >
                <option value="">— niemand —</option>
                {(usersQ.data?.items ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.displayName} ({u.email})
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Notiz (intern)</label>
            <Input
              value={notizen}
              onChange={(e) => setNotizen(e.target.value)}
              placeholder="z.B. Kontextinfo zur Anfrage"
            />
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button type="submit" disabled={triageMutation.isPending}>
              Triage speichern
            </Button>
            {a.status === 'zugeordnet' ? (
              <>
                <span className="mx-2 self-center text-xs text-slate-400">|</span>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={convertMutation.isPending}
                  onClick={() => convertMutation.mutate('auftrag')}
                >
                  → Auftrag
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={convertMutation.isPending}
                  onClick={() => convertMutation.mutate('projekt')}
                >
                  → Projekt
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={convertMutation.isPending}
                  onClick={() => convertMutation.mutate('reklamation')}
                >
                  → Reklamation
                </Button>
              </>
            ) : null}
            <span className="ml-auto" />
            <Button type="button" variant="danger" onClick={handleClose}>
              Schliessen
            </Button>
          </div>
          <p className="text-xs text-slate-500 pt-1">
            Konvertierung erzeugt erst in Phase 5 einen echten Folge-Vorgang. Aktuell wird die
            Anfrage nur als "konvertiert" markiert.
          </p>
        </form>
      ) : null}
    </div>
  );
}

function DefRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="text-sm">
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="text-slate-900">{children}</dd>
    </div>
  );
}
